import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BackBar from '../components/BackBar';

const Guidelines = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const isNewUser = searchParams.get('welcome') === 'true';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleStartProject = () => {
    navigate('/project/new');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <BackBar to="/dashboard" label="Dashboard" />

      {/* Welcome Banner for New Users */}
      {isNewUser && (
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold">Welcome to OpWMP, {user?.full_name}!</h2>
                <p className="text-green-100">Please review these guidelines before creating your first project.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          {/* Page Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6">
            <h1 className="text-2xl font-bold text-white">WMP App – User Guidelines</h1>
            <p className="text-blue-100 mt-1">Everything you need to know to create your Waste Management Plan</p>
          </div>

          <div className="px-8 py-6 space-y-8">
            {/* What this app does */}
            <section>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">1</span>
                What this app does
              </h2>
              <p className="mt-2 text-gray-600 ml-10">
                Generates planning-stage Waste Management Plans (WMP / SWMMP / DCWMP) using industry-accepted assumptions suitable for council submission.
              </p>
            </section>

            {/* Who it's for */}
            <section>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">2</span>
                Who it&apos;s for
              </h2>
              <p className="mt-2 text-gray-600 ml-10">
                Planners, designers, developers and builders preparing planning or building approvals.
              </p>
            </section>

            {/* When to use it */}
            <section>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">3</span>
                When to use it
              </h2>
              <div className="mt-2 text-gray-600 ml-10">
                <p>Use where a council requires a WMP.</p>
              </div>
            </section>

            {/* How quantities are calculated */}
            <section>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">4</span>
                How quantities are calculated
              </h2>
              <p className="mt-2 text-gray-600 ml-10">
                All waste quantities are estimates only, based on typical generation rates and planning-level assumptions. Actual volumes may vary.
              </p>
            </section>

            {/* What you'll need */}
            <section>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">5</span>
                What you&apos;ll need
              </h2>
              <ul className="mt-2 text-gray-600 ml-10 list-disc list-inside space-y-1">
                <li>Development type</li>
                <li>Dwelling/bed numbers or floor areas</li>
                <li>Demolition and construction scope</li>
                <li>Waste storage/collection details</li>
              </ul>
            </section>

            {/* How it works */}
            <section>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">6</span>
                How it works
              </h2>
              <div className="mt-3 ml-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">1</span>
                  <span className="text-gray-700">Enter project details</span>
                </div>
                <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">2</span>
                  <span className="text-gray-700">Complete waste sections</span>
                </div>
                <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">3</span>
                  <span className="text-gray-700">Upload supporting documents</span>
                </div>
                <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">4</span>
                  <span className="text-gray-700">Generate and download your WMP</span>
                </div>
              </div>
            </section>

            {/* Supporting documents */}
            <section>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">7</span>
                Supporting documents (optional)
              </h2>
              <div className="mt-2 text-gray-600 ml-10">
                <p>Upload swept paths, bin room layouts, site photos or reports.</p>
                <p className="mt-2"><strong>Accepted formats:</strong> JPG, PNG, TIFF</p>
                <p className="text-sm text-gray-500 mt-1">Files are included as Appendix 4.</p>
              </div>
            </section>

            {/* Updates & RFIs */}
            <section>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">8</span>
                Updates & RFIs
              </h2>
              <div className="mt-2 text-gray-600 ml-10 bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800">
                  <strong>Free updates:</strong> Edit and regenerate your WMP for 30 days from payment at no cost.
                </p>
                <p className="mt-2 text-green-700 text-sm">
                  After 30 days, further updates or council RFIs may incur a small additional fee.
                </p>
              </div>
            </section>

            {/* Important notes */}
            <section>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </span>
                Important notes
              </h2>
              <ul className="mt-2 text-gray-600 ml-10 space-y-2">
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Plans rely on user-provided information
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Drawings and site conditions are not verified
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Council approval remains at the authority&apos;s discretion
                </li>
              </ul>
            </section>

            {/* Best practice */}
            <section className="bg-blue-50 -mx-8 px-8 py-6 border-t border-blue-100">
              <h2 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
                Best practice
              </h2>
              <p className="mt-2 text-blue-800">
                Be conservative with estimates, align waste locations with plans, and regenerate the WMP if designs change.
              </p>
            </section>
          </div>
        </div>

        {/* Back to Dashboard Button */}
        <div className="mt-6 text-center space-y-3">
          {isNewUser ? (
            <>
              <button
                onClick={handleStartProject}
                className="inline-flex items-center px-8 py-4 border border-transparent rounded-md shadow-lg text-base font-medium text-white bg-green-600 hover:bg-green-700 transition-colors"
              >
                <svg className="-ml-1 mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                I Understand - Start My First Project
              </button>
              <p className="text-sm text-gray-500">
                Or <button onClick={() => navigate('/dashboard')} className="text-blue-600 hover:underline">go to Dashboard</button> to explore first
              </p>
            </>
          ) : (
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Dashboard
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

export default Guidelines;
